"use client";

import { useState, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Link2, Terminal, Loader2, FileJson, Check, X, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { getAuthToken } from "@/lib/auth";
import { API_ENDPOINTS } from "@/lib/api-config";
import { parseCurlCommand } from "./curl-parser";
import type { ConnectionConfig } from "../connection-studio";

interface GeneralTabProps {
  formData: ConnectionConfig;
  onChange: (updates: Partial<ConnectionConfig>) => void;
  /** Auto-save the connection (silently) and return the new connection ID. */
  onEnsureConnection?: (opts?: {
    nameOverride?: string;
    silent?: boolean;
    configOverrides?: Partial<ConnectionConfig>;
  }) => Promise<string | null>;
}

interface SpecInfo {
  title: string;
  version: string;
  description: string;
  base_url: string | null;
  auth_type: string;
  auth_requirements: Record<string, string>;
  endpoint_count: number;
}

const CONNECTION_TYPES = [
  { value: "rest_api", label: "REST API", icon: "🔌" },
  { value: "graphql", label: "GraphQL", icon: "◈" },
  { value: "database", label: "Database", icon: "🗄️" },
  { value: "file_share", label: "File Share (SMB)", icon: "📁" },
  { value: "webhook", label: "Webhook", icon: "🪝" },
  { value: "custom", label: "Custom", icon: "⚙️" },
];

const ACCEPTED_EXTENSIONS = [".json", ".yaml", ".yml"];

export function GeneralTab({ formData, onChange, onEnsureConnection }: GeneralTabProps) {
  const [showCurlInput, setShowCurlInput] = useState(false);
  const [curlCommand, setCurlCommand] = useState("");
  const [curlError, setCurlError] = useState<string | null>(null);
  const [isParsingCurl, setIsParsingCurl] = useState(false);
  const [openApiUrl, setOpenApiUrl] = useState("");
  const [isLoadingSpec, setIsLoadingSpec] = useState(false);
  const [specMode, setSpecMode] = useState<"url" | "file">("file");
  const [specFile, setSpecFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [parsedSpec, setParsedSpec] = useState<SpecInfo | null>(
    () => (formData.config?.parsed_spec as SpecInfo | undefined) ?? null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleParseCurl = async () => {
    if (!curlCommand.trim()) return;

    setIsParsingCurl(true);
    setCurlError(null);

    try {
      const parsed = parseCurlCommand(curlCommand);
      
      onChange({
        base_url: parsed.base_url,
        auth_type: parsed.auth_type,
        auth_config: parsed.auth_config,
        headers: parsed.headers,
        config: { ...formData.config, ...parsed.config },
      });

      setShowCurlInput(false);
      setCurlCommand("");
    } catch (error) {
      setCurlError(error instanceof Error ? error.message : "Failed to parse curl command");
    } finally {
      setIsParsingCurl(false);
    }
  };

  const handleLoadOpenApiSpec = async () => {
    if (!openApiUrl.trim()) return;

    setIsLoadingSpec(true);

    try {
      // Store the OpenAPI URL in config for later processing
      onChange({
        config: {
          ...formData.config,
          openapi_url: openApiUrl,
        },
      });

      // Try to extract base URL from OpenAPI spec URL
      const url = new URL(openApiUrl);
      if (!formData.base_url) {
        // Use origin as a starting point
        onChange({ base_url: url.origin });
      }
    } catch (error) {
      console.error("Failed to process OpenAPI URL:", error);
    } finally {
      setIsLoadingSpec(false);
    }
  };

  const validateFile = (file: File): string | null => {
    const name = file.name.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext))) {
      return "File must be .json, .yaml, or .yml";
    }
    if (file.size > 50 * 1024 * 1024) {
      return "File size must be under 50MB";
    }
    return null;
  };

  const handleFileSelect = useCallback((file: File) => {
    const error = validateFile(file);
    if (error) {
      setUploadError(error);
      return;
    }
    setSpecFile(file);
    setUploadError(null);
    setParsedSpec(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleUploadSpec = async () => {
    if (!specFile) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const token = getAuthToken();

      // --- Step 1: Parse the spec (no connection_id yet) ---
      const formDataUpload = new FormData();
      formDataUpload.append("file", specFile);

      const parseResponse = await fetch(API_ENDPOINTS.connectionUploadSpec(), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formDataUpload,
      });

      if (!parseResponse.ok) {
        const errData = await parseResponse.json().catch(() => null);
        let errMsg = `Upload failed (${parseResponse.status})`;
        if (errData?.detail) {
          errMsg =
            typeof errData.detail === "string"
              ? errData.detail
              : JSON.stringify(errData.detail);
        }
        throw new Error(errMsg);
      }

      const data = await parseResponse.json();
      const info: SpecInfo = data.info;
      setParsedSpec(info);

      const updates: Partial<ConnectionConfig> = {
        config: {
          ...formData.config,
          openapi_spec_uploaded: true,
          openapi_spec_title: info.title,
          openapi_spec_version: info.version,
          openapi_endpoint_count: info.endpoint_count,
          parsed_spec: info,
        },
      };

      if (info.base_url && !formData.base_url) {
        updates.base_url = info.base_url;
      }

      if (info.auth_type && info.auth_type !== "none" && formData.auth_type === "none") {
        updates.auth_type = info.auth_type;
        if (info.auth_requirements && Object.keys(info.auth_requirements).length > 0) {
          updates.auth_config = {
            ...formData.auth_config,
            ...info.auth_requirements,
          };
        }
      }

      if (info.description && !formData.description) {
        updates.description = info.description;
      }

      onChange(updates);

      // --- Step 2: Auto-save connection to get a connection_id ---
      // Pass the freshly-parsed updates as configOverrides so the save
      // uses the most up-to-date values (React state hasn't re-rendered yet).
      const effectiveBaseUrl = (updates.base_url as string) || formData.base_url;
      const effectiveName = formData.name;

      if (onEnsureConnection && effectiveName && effectiveBaseUrl) {
        const connectionId = await onEnsureConnection({
          silent: true,
          configOverrides: updates,
        });

        if (connectionId) {
          // --- Step 3: Re-upload spec WITH connection_id so the backend
          //     runs the full pipeline (RAG sync, enrichment, embeddings) ---
          const linkedUpload = new FormData();
          linkedUpload.append("file", specFile);

          fetch(API_ENDPOINTS.connectionUploadSpec(connectionId), {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: linkedUpload,
          }).catch((err) =>
            console.warn("[general-tab] Background spec re-upload failed:", err),
          );
        }
      }
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const handleClearSpec = () => {
    setSpecFile(null);
    setParsedSpec(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    const { parsed_spec: _, ...restConfig } = formData.config as Record<string, unknown>;
    onChange({ config: restConfig });
  };

  return (
    <div className="space-y-6">
      {/* Connection Name */}
      <div className="space-y-2">
        <Label className="text-white/80">
          Connection Name <span className="text-rose-400">*</span>
        </Label>
        <Input
          value={formData.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="My API Connection"
          className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
        />
      </div>

      {/* Connection Type */}
      <div className="space-y-2">
        <Label className="text-white/80">Connection Type</Label>
        <div className="flex flex-wrap gap-2">
          {CONNECTION_TYPES.map((type) => (
            <Badge
              key={type.value}
              variant={formData.type === type.value ? "default" : "outline"}
              className={cn(
                "cursor-pointer px-3 py-2 text-sm",
                formData.type === type.value
                  ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                  : "border-white/10 bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
              )}
              onClick={() => onChange({ type: type.value as ConnectionConfig["type"] })}
            >
              <span className="mr-1.5">{type.icon}</span>
              {type.label}
            </Badge>
          ))}
        </div>
      </div>

      {formData.type === "file_share" && (
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
          <p className="text-sm text-amber-300/90">
            SMB file share connections use the Auth tab for server, share, and credential settings.
            After connecting, start a crawl to discover and index files as endpoints.
          </p>
        </div>
      )}

      {formData.type === "database" && (
        <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 p-4">
          <p className="text-sm text-sky-300/90">
            Database connections use the Auth tab for host, port, database name, and credentials.
            After connecting, tables are discovered as endpoints and columns as fields.
          </p>
        </div>
      )}

      {/* Base URL - hidden for file_share and database */}
      {!["file_share", "database"].includes(formData.type) && (
      <div className="space-y-2">
        <Label className="text-white/80">
          {formData.type === "webhook" ? "Webhook URL" : "Base URL"}{" "}
          <span className="text-rose-400">*</span>
        </Label>
        <Input
          value={formData.base_url}
          onChange={(e) => onChange({ base_url: e.target.value })}
          placeholder={
            formData.type === "graphql"
              ? "https://api.example.com/graphql"
              : formData.type === "webhook"
                ? "https://your-app.com/webhooks/incoming"
                : "https://api.example.com/v1"
          }
          className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
        />
        <p className="text-xs text-white/40">
          {formData.type === "webhook"
            ? "The URL that will receive webhook payloads"
            : formData.type === "graphql"
              ? "The GraphQL endpoint URL"
              : "The base URL for all API requests (without trailing slash)"}
        </p>
      </div>
      )}

      {/* Quick Import Options - hidden for file_share, database, and graphql */}
      {!["file_share", "database", "graphql"].includes(formData.type) && (
      <div className="space-y-3">
        <Label className="text-white/80">Quick Import</Label>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCurlInput(!showCurlInput)}
            className={cn(
              "border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white",
              showCurlInput && "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
            )}
          >
            <Terminal className="mr-2 h-4 w-4" />
            Paste cURL
          </Button>
        </div>

        {/* cURL Input */}
        {showCurlInput && (
          <div className="space-y-3 rounded-lg border border-white/10 bg-white/5 p-4">
            <Label className="text-white/70">Paste your cURL command</Label>
            <Textarea
              value={curlCommand}
              onChange={(e) => setCurlCommand(e.target.value)}
              placeholder={`curl -X GET "https://api.example.com/users" \\
  -H "Authorization: Bearer your_token" \\
  -H "Content-Type: application/json"`}
              className="min-h-[120px] border-white/10 bg-white/5 font-mono text-sm text-white placeholder:text-white/40"
            />
            {curlError && (
              <p className="text-sm text-rose-400">{curlError}</p>
            )}
            <div className="flex gap-2">
              <Button
                onClick={handleParseCurl}
                disabled={!curlCommand.trim() || isParsingCurl}
                className="bg-emerald-500 text-white hover:bg-emerald-600"
              >
                {isParsingCurl ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Parsing...
                  </>
                ) : (
                  "Parse & Apply"
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowCurlInput(false);
                  setCurlCommand("");
                  setCurlError(null);
                }}
                className="text-white/60 hover:text-white"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* OpenAPI Specification - hidden for file_share and database */}
      {!["file_share", "database"].includes(formData.type) && (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-white/80">OpenAPI Specification (Optional)</Label>
          <div className="flex gap-1 rounded-md border border-white/10 bg-white/5 p-0.5">
            <button
              type="button"
              onClick={() => setSpecMode("file")}
              className={cn(
                "rounded px-2.5 py-1 text-xs transition-colors",
                specMode === "file"
                  ? "bg-white/10 text-white"
                  : "text-white/50 hover:text-white/70"
              )}
            >
              <Upload className="mr-1.5 inline-block h-3 w-3" />
              Upload
            </button>
            <button
              type="button"
              onClick={() => setSpecMode("url")}
              className={cn(
                "rounded px-2.5 py-1 text-xs transition-colors",
                specMode === "url"
                  ? "bg-white/10 text-white"
                  : "text-white/50 hover:text-white/70"
              )}
            >
              <Link2 className="mr-1.5 inline-block h-3 w-3" />
              URL
            </button>
          </div>
        </div>

        {specMode === "file" ? (
          <div className="space-y-3">
            {/* File drop zone */}
            {!parsedSpec && (
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  "flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed p-6 transition-colors",
                  isDragging
                    ? "border-emerald-500/50 bg-emerald-500/10"
                    : specFile
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : "border-white/15 bg-white/[0.02] hover:border-white/25 hover:bg-white/5"
                )}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,.yaml,.yml"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                  }}
                />
                {specFile ? (
                  <div className="flex items-center gap-3">
                    <FileJson className="h-5 w-5 text-emerald-400" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-white">{specFile.name}</p>
                      <p className="text-xs text-white/40">
                        {(specFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearSpec();
                      }}
                      className="ml-2 h-7 w-7 p-0 text-white/40 hover:text-white"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="mb-2 h-5 w-5 text-white/30" />
                    <p className="text-sm text-white/50">
                      Drop your spec file here or{" "}
                      <span className="text-emerald-400">browse</span>
                    </p>
                    <p className="mt-1 text-xs text-white/30">
                      JSON, YAML (.json, .yaml, .yml)
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Upload button */}
            {specFile && !parsedSpec && (
              <Button
                onClick={handleUploadSpec}
                disabled={isUploading}
                className="w-full bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Parsing specification...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload & Parse
                  </>
                )}
              </Button>
            )}

            {/* Parsed spec info */}
            {parsedSpec && (
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-emerald-400" />
                    <span className="text-sm font-medium text-emerald-400">
                      Spec parsed successfully
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearSpec}
                    className="h-7 text-xs text-white/40 hover:text-white"
                  >
                    Clear
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  <div className="text-white/50">Title</div>
                  <div className="text-white/80">{parsedSpec.title}</div>
                  {parsedSpec.version && (
                    <>
                      <div className="text-white/50">Version</div>
                      <div className="text-white/80">{parsedSpec.version}</div>
                    </>
                  )}
                  <div className="text-white/50">Endpoints</div>
                  <div className="text-white/80">{parsedSpec.endpoint_count} GET endpoints</div>
                  {parsedSpec.auth_type !== "none" && (
                    <>
                      <div className="text-white/50">Auth</div>
                      <div className="text-white/80">
                        {parsedSpec.auth_type.replace(/_/g, " ")}
                      </div>
                    </>
                  )}
                  {parsedSpec.base_url && (
                    <>
                      <div className="text-white/50">Base URL</div>
                      <div className="truncate text-white/80">{parsedSpec.base_url}</div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Upload error */}
            {uploadError && (
              <div className="flex items-start gap-2 rounded-lg border border-rose-500/20 bg-rose-500/5 p-3">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" />
                <p className="text-xs text-rose-400">{uploadError}</p>
              </div>
            )}
          </div>
        ) : (
          /* URL mode */
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                value={openApiUrl}
                onChange={(e) => setOpenApiUrl(e.target.value)}
                placeholder="https://api.example.com/openapi.json"
                className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
              />
              <Button
                onClick={handleLoadOpenApiSpec}
                disabled={!openApiUrl.trim() || isLoadingSpec}
                variant="outline"
                className="border-white/10 bg-white/5 text-white hover:bg-white/10"
              >
                {isLoadingSpec ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Link2 className="h-4 w-4" />
                )}
              </Button>
            </div>
            {formData.config?.openapi_url && (
              <p className="text-xs text-emerald-400">
                OpenAPI spec linked: {formData.config.openapi_url as string}
              </p>
            )}
          </div>
        )}
      </div>
      )}

      {/* Description */}
      <div className="space-y-2">
        <Label className="text-white/80">Description</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Describe what this connection is used for..."
          className="min-h-[80px] border-white/10 bg-white/5 text-white placeholder:text-white/40"
        />
      </div>
    </div>
  );
}
